import React, { useEffect, useState } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { apiGet } from '../../services/api';
import { avatarColor, employeeCountLabel } from '../../utils/companyHelpers';
import JobCard from '../jobs/JobCard';

function formatLocation(city, country) {
  const parts = [city, country].filter((p) => p && String(p).trim() !== '');
  return parts.join(', ');
}

function openPositionsLabel(count) {
  const n = Number(count) || 0;
  if (n === 1) return '1 open position';
  return `${n} open positions`;
}

export default function CompanyProfile() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [company, setCompany] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobCount, setJobCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setNotFound(false);
      setCompany(null);
      setJobs([]);
      setJobCount(0);

      try {
        const res = await apiGet(`/api/v1/companies/${encodeURIComponent(slug)}`);
        if (cancelled) return;

        if (res.status === 404) {
          setNotFound(true);
          return;
        }

        if (!res.ok) {
          setNotFound(true);
          return;
        }

        const json = await res.json();
        setCompany(json.data?.company || null);
        setJobs(Array.isArray(json.data?.jobs) ? json.data.jobs : []);
        setJobCount(json.meta?.job_count ?? (json.data?.jobs?.length || 0));
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (slug) {
      load();
    } else {
      setLoading(false);
      setNotFound(true);
    }

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <Container className="py-5 text-center" data-testid="rwm-company-profile-loading">
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  if (notFound || !company) {
    return (
      <Container className="py-5 text-center" data-testid="rwm-company-profile-not-found">
        <p className="text-muted mb-3">Company not found.</p>
        <Link to="/jobs" className="rwm-job-detail__browse-link text-decoration-none">
          Browse all remote jobs →
        </Link>
      </Container>
    );
  }

  const logoUrl = company.logo_path || null;
  const initial = (company.name && company.name.trim()[0]) || '?';
  const locationLine = formatLocation(company.city, company.country);
  const employees = employeeCountLabel(company.employee_count);
  const website = company.website;
  const linkedin = company.linkedin_url;
  const descriptionHtml = company.description && String(company.description).trim();

  return (
    <Container className="py-4 rwm-company-profile">
      <header className="mb-4 pb-4 border-bottom" style={{ borderColor: 'var(--rwm-border)' }} data-testid="rwm-company-profile-header">
        <div className="d-flex flex-wrap align-items-start gap-3">
          <div style={{ width: 72, height: 72 }}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={company.name || 'Company'}
                width={72}
                height={72}
                className="rounded"
                style={{ objectFit: 'cover', width: 72, height: 72 }}
              />
            ) : (
              <div
                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-semibold"
                style={{
                  width: 72,
                  height: 72,
                  backgroundColor: avatarColor(company.name || ''),
                  fontSize: 28,
                }}
              >
                {initial.toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-grow-1">
            <h1 className="h2 fw-semibold mb-2" style={{ color: 'var(--rwm-dark)' }}>
              {company.name}
            </h1>
            {locationLine ? (
              <p className="mb-1" style={{ color: 'var(--rwm-slate)' }}>
                {locationLine}
              </p>
            ) : null}
            {employees ? (
              <p className="mb-2 small" style={{ color: 'var(--rwm-slate)' }}>
                {employees}
              </p>
            ) : null}
            <div className="d-flex flex-wrap gap-3">
              {website && String(website).trim() !== '' ? (
                <a
                  href={website.startsWith('http') ? website : `https://${website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rwm-job-detail__browse-link text-decoration-none small"
                >
                  Visit website →
                </a>
              ) : null}
              {linkedin && String(linkedin).trim() !== '' ? (
                <a
                  href={linkedin.startsWith('http') ? linkedin : `https://${linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rwm-job-detail__browse-link text-decoration-none small"
                >
                  LinkedIn →
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      {(descriptionHtml || company.founded_year || company.industry) ? (
        <section className="mb-5" data-testid="rwm-company-profile-about">
          <h2 className="h5 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
            About
          </h2>
          {descriptionHtml ? (
            <div
              className="mb-3"
              style={{ color: 'var(--rwm-slate)' }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(descriptionHtml, {
                  ALLOWED_TAGS: ['p', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'br', 'h2', 'h3', 'h4', 'a'],
                  ALLOWED_ATTR: ['href', 'target', 'rel'],
                }),
              }}
            />
          ) : null}
          {company.founded_year ? (
            <p className="mb-1 small" style={{ color: 'var(--rwm-slate)' }}>
              Founded {company.founded_year}
            </p>
          ) : null}
          {company.industry ? (
            <p className="mb-0 small" style={{ color: 'var(--rwm-slate)' }}>
              {company.industry}
            </p>
          ) : null}
        </section>
      ) : null}

      <section data-testid="rwm-company-profile-jobs">
        <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-3">
          <h2 className="h5 fw-semibold mb-0" style={{ color: 'var(--rwm-dark)' }}>
            Open Positions
          </h2>
          <span className="small" style={{ color: 'var(--rwm-slate)' }} data-testid="rwm-company-profile-job-count">
            {openPositionsLabel(jobCount)}
          </span>
        </div>
        {jobs.length === 0 ? (
          <p className="text-muted mb-0">No open positions at the moment.</p>
        ) : (
          jobs.map((job) => (
            <JobCard key={job.id} job={job} showCompany={false} />
          ))
        )}
      </section>
    </Container>
  );
}
